import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { NoAccess, SubNav } from "@/app/Shell";
import { GATE, useSession } from "@/auth/session";
import { FleetOverviewPage } from "@/modules/fleet/OverviewPage";
import { FleetPage } from "@/modules/fleet/FleetPage";

/** Fleet — commanding assets. Its OWN module, and that is a correctness fix.
 *
 *  WHY IT MOVED OUT OF ASSET DATA. Dattatray: *"Asset data can be seen by many
 *  people but asset control shall be accessible to only a few!"* The
 *  permission model already said exactly that, and the UI contradicted it:
 *
 *      basic  company_admin   asset_data=True   fleet=False
 *      pro    company_admin   asset_data=True   fleet=True
 *
 *  `GATE_ASSET_DATA` is in every plan including BASIC; `GATE_FLEET` is PRO-only
 *  and `IMMOBILIZE` is narrower still. With Asset Control living inside the
 *  Asset Data module, a BASIC user SAW the tab and every button 403'd — the nav
 *  said yes while the server said no. Worse, the Fleet rail entry existed all
 *  along (`Shell.tsx`, `wired: false`) with a catalogue description reading
 *  "Live map and commands", so the right home was already named and empty.
 *
 *  THE MODULE GUARDS ITSELF, which no other module does — `App.tsx` mounts
 *  every module ungated and relies on the rail hiding what you cannot reach.
 *  That is fine for a read-only module and not fine here: the rail is
 *  cosmetic, and a TYPED `/fleet/control` would otherwise render the whole
 *  page to a BASIC account and let it discover the answer from a row of 403s.
 *  `require_cap(GATE_FLEET)` on the router is still the enforcement; this is
 *  what stops the console asking.
 *
 *  BOTH TABS ARE UNGATED BEYOND THE MODULE, deliberately. Reading what has
 *  been commanded is not commanding: a fleet operator who may open Fleet but
 *  not immobilise still needs to see that somebody did. The WRITE controls on
 *  both pages sit behind `CAP.IMMOBILIZE` individually — which is why neither
 *  page is gated as a whole.
 */
export function FleetModule() {
  const { can } = useSession();
  if (!can(GATE.FLEET)) return <NoAccess label="Fleet" />;
  return (
    <>
      {/* ONE TAB, AND THE TAB ROW STAYS. Asset Control stopped being a second
          subpage once the command page could target a single asset or many - 
          but removing the shared SubNav along with it was wrong, and
          Dattatray caught that too: *"In fleet becasuse there is one sub
          page: You have removed the sub page selection tab itself!"* Every
          other module shows where you are; one that silently drops its tab
          row reads as a different kind of page, and the row is also where the
          next command surface will appear. */}
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        {/* TWO TABS NOW. Overview arrived from Asset Data on 2026-10-23 --
            a map of many devices and a fleet-wide firmware distribution,
            which is this module's question and not Asset Data's. The comment
            above was right that this row is "where the next command surface
            will appear"; it appeared. Overview leads because it is the
            at-a-glance answer and Commands is the action you take after it. */}
        <SubNav base="/fleet" tabs={[
          { path: "overview", label: "Overview" },
          { path: "commands", label: "Commands" },
        ]} />
        <div style={{ marginLeft: "auto", paddingBottom: 9, paddingRight: 20 }}>
        </div>
      </div>
      <Routes>
        <Route path="overview" element={<FleetOverviewPage />} />
        <Route path="commands" element={<FleetPage />} />
        {/* `control` WAS A COMMAND SURFACE, so it lands on Commands and not on
            the new Overview. Explicit rather than left to the catch-all: once
            this module has two pages, "anything unknown goes to the one page"
            stops being a rule and becomes a coin toss, and a bookmark from the
            Asset Control era is asking to act on a vehicle. */}
        <Route path="control" element={<RedirectToCommands />} />
        {/* ASSET CONTROL WAS A SUBPAGE AND IS NOT ANY MORE. Dattatray:
            *"Asset control subpage doe snot make any sesnse!"* — right, once
            the command page can target a single asset with the same filters, a
            separate page for one asset is a second way to do one thing.
            Redirected rather than dropped: the fleet map's popups and anything
            bookmarked point at it, and the SPA catch-all answers an unknown
            path with index.html, so a dead link reads as a BLANK PAGE, not a
            404. The `?asset=` query survives, which is the whole address. */}
        <Route path="*" element={<FleetLanding />} />
      </Routes>
    </>
  );
}

/** Where `/fleet` and any unknown subpath land.
 *
 *  `?asset=` STILL DECIDES. A link carrying one asset is asking to act on that
 *  asset, so it goes to Commands with the query intact -- the fleet map's
 *  popups and the old Asset Control bookmarks are all of that shape, and
 *  dropping the query would land someone on a page about the whole fleet when
 *  they clicked one vehicle. Without an asset, Overview is the landing page:
 *  it is the at-a-glance answer, and Commands is what you do after reading it.
 */
function FleetLanding() {
  const [params] = useSearchParams();
  const asset = params.get("asset");
  return <Navigate replace to={
    asset ? `/fleet/commands?asset=${encodeURIComponent(asset)}` : "/fleet/overview"} />;
}

/** The old Asset Control path, and anything carrying one asset -- straight to
 *  Commands with the query intact. */
function RedirectToCommands() {
  const [params] = useSearchParams();
  const asset = params.get("asset");
  return <Navigate replace to={
    asset ? `/fleet/commands?asset=${encodeURIComponent(asset)}` : "/fleet/commands"} />;
}
